/**
 * EMR Expression Evaluator Utility
 *
 * Evaluates simple expressions for action visibility and enablement.
 * Supports common operators and the exists() function.
 *
 * Single Responsibility: Expression evaluation
 *
 * Examples:
 * - evaluateExpression("item.state == 'PENDING'", { item: { state: 'PENDING' } }) → true
 * - evaluateExpression("item.state in ['PENDING','ADDED']", { item: { state: 'ADDED' } }) → true
 * - evaluateExpression("exists(items, i -> i.state == 'PENDING')", { items: [...] }) → true
 */

/**
 * Evaluate an expression string against a context object
 * @param {string} expression - Expression to evaluate (e.g., "item.state == 'PENDING'")
 * @param {Object} context - Context object with variables (e.g., { item, items, sectionData })
 * @returns {boolean} - Result of the expression
 */
export function evaluateExpression(expression, context = {}) {
  if (!expression || typeof expression !== 'string') {
    return true // No expression means always visible/enabled
  }

  const trimmedExpr = expression.trim()

  // Handle exists() function
  if (trimmedExpr.startsWith('exists(')) {
    return evaluateExistsFunction(trimmedExpr, context)
  }

  // Handle simple comparisons
  return evaluateSimpleComparison(trimmedExpr, context)
}

/**
 * Evaluate exists() function
 * Pattern: exists(arrayName, varName -> condition)
 * Example: exists(items, i -> i.state == 'PENDING')
 */
function evaluateExistsFunction(expression, context) {
  // Parse: exists(items, i -> i.state == 'PENDING')
  const match = expression.match(/exists\(\s*(\w+)\s*,\s*(\w+)\s*->\s*(.+)\s*\)/)

  if (!match) {
    console.warn('[emrExpressionEvaluator] Invalid exists() syntax:', expression)
    return false
  }

  const [, arrayName, varName, condition] = match
  const array = context[arrayName]

  if (!Array.isArray(array)) {
    console.warn('[emrExpressionEvaluator] Array not found in context:', arrayName)
    return false
  }

  // Check if any item in array satisfies the condition
  return array.some((item) => {
    const itemContext = { ...context, [varName]: item }
    return evaluateSimpleComparison(condition.trim(), itemContext)
  })
}

/**
 * Evaluate simple comparison expressions
 * Supports: ==, !=, in, >, <, >=, <=
 */
function evaluateSimpleComparison(expression, context) {
  // Handle "in" operator: item.state in ['PENDING','ADDED']
  if (expression.includes(' in ')) {
    return evaluateInOperator(expression, context)
  }

  // Handle equality operators: ==, !=
  if (expression.includes('==')) {
    const [left, right] = expression.split('==').map(s => s.trim())
    return getValue(left, context) == getValue(right, context)
  }

  if (expression.includes('!=')) {
    const [left, right] = expression.split('!=').map(s => s.trim())
    return getValue(left, context) != getValue(right, context)
  }

  // Handle comparison operators: >=, <=, >, <
  if (expression.includes('>=')) {
    const [left, right] = expression.split('>=').map(s => s.trim())
    return getValue(left, context) >= getValue(right, context)
  }

  if (expression.includes('<=')) {
    const [left, right] = expression.split('<=').map(s => s.trim())
    return getValue(left, context) <= getValue(right, context)
  }

  if (expression.includes('>')) {
    const [left, right] = expression.split('>').map(s => s.trim())
    return getValue(left, context) > getValue(right, context)
  }

  if (expression.includes('<')) {
    const [left, right] = expression.split('<').map(s => s.trim())
    return getValue(left, context) < getValue(right, context)
  }

  // Fallback: treat as boolean variable
  return !!getValue(expression, context)
}

/**
 * Evaluate "in" operator
 * Pattern: item.state in ['PENDING','ADDED','MODIFIED']
 */
function evaluateInOperator(expression, context) {
  const [left, right] = expression.split(' in ').map(s => s.trim())
  const value = getValue(left, context)

  // Parse array literal: ['PENDING','ADDED']
  const arrayMatch = right.match(/\[(.*)\]/)
  if (!arrayMatch) {
    console.warn('[emrExpressionEvaluator] Invalid in operator syntax:', expression)
    return false
  }

  const arrayItems = arrayMatch[1]
    .split(',')
    .map(s => s.trim())
    .map(s => s.replace(/^['"]|['"]$/g, '')) // Remove quotes

  return arrayItems.includes(String(value))
}

/**
 * Get value from context using dot notation
 * Examples:
 * - "item.state" → context.item.state
 * - "'PENDING'" → "PENDING" (string literal)
 * - "123" → 123 (number literal)
 */
function getValue(path, context) {
  // Handle string literals: 'PENDING' or "PENDING"
  if ((path.startsWith("'") && path.endsWith("'")) ||
      (path.startsWith('"') && path.endsWith('"'))) {
    return path.slice(1, -1)
  }

  // Handle number literals
  if (!isNaN(path)) {
    return Number(path)
  }

  // Handle boolean literals
  if (path === 'true') return true
  if (path === 'false') return false
  if (path === 'null') return null
  if (path === 'undefined') return undefined

  // Handle dot notation: item.state
  const parts = path.split('.')
  let value = context

  for (const part of parts) {
    if (value == null) return undefined
    value = value[part]
  }

  return value
}

/**
 * Evaluate multiple expressions with AND logic
 * @param {string[]} expressions - Array of expressions
 * @param {Object} context - Context object
 * @returns {boolean} - True if all expressions evaluate to true
 */
export function evaluateAllExpressions(expressions, context) {
  if (!expressions || expressions.length === 0) {
    return true
  }

  return expressions.every(expr => evaluateExpression(expr, context))
}
